import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CapabilityStateControl } from "@/components/capabilities/CapabilityStateControl";
import { capabilityKeys } from "@/hooks/useCapabilities";
import {
  createCapabilityOverride,
  deleteCapabilityOverride,
  type CapabilityOverride,
} from "@/services/capabilitiesApi";
import type { TripwirePin } from "@/utils/tripwireBadge";
import {
  OVERRIDE_REMOVED_THEN_FAILED,
  OverrideRemovedThenFailedError,
  syncCapabilityPin,
} from "@/utils/syncCapabilityPin";
import { toast } from "sonner";

const ABSOLUTE = "scraper.tripwire";
const SCHEDULED = "scraper.tripwire.scheduled";

function pinFromRow(row: CapabilityOverride | undefined): TripwirePin {
  if (!row) return "inherit";
  return row.state === "allow" ? "allow" : "deny";
}

export function TripwireOverrideDialog({
  open,
  onOpenChange,
  sourceId,
  sourceName,
  absoluteRow,
  scheduledRow,
  absoluteGlobalDeny,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceId: string | null;
  sourceName: string;
  absoluteRow: CapabilityOverride | undefined;
  scheduledRow: CapabilityOverride | undefined;
  absoluteGlobalDeny: boolean;
}) {
  const queryClient = useQueryClient();
  const [absolutePin, setAbsolutePin] = useState<TripwirePin>("inherit");
  const [scheduledPin, setScheduledPin] = useState<TripwirePin>("inherit");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    const opened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opened) return;
    setAbsolutePin(pinFromRow(absoluteRow));
    setScheduledPin(pinFromRow(scheduledRow));
    setReason("");
  }, [open, absoluteRow, scheduledRow]);

  const writesNeedReason = useMemo(() => {
    const absChanged = absolutePin !== pinFromRow(absoluteRow);
    const schedChanged = scheduledPin !== pinFromRow(scheduledRow);
    const absWrite = absChanged && absolutePin !== "inherit";
    const schedWrite = schedChanged && scheduledPin !== "inherit";
    return absWrite || schedWrite;
  }, [absolutePin, scheduledPin, absoluteRow, scheduledRow]);

  const handleSubmit = async () => {
    if (!sourceId) return;
    if (writesNeedReason && !reason.trim()) return;
    setPending(true);
    try {
      const absChanged = await syncCapabilityPin({
        capability: ABSOLUTE,
        scopeType: "source",
        scopeId: sourceId,
        current: absoluteRow,
        desired: absolutePin,
        reason: reason.trim(),
        create: createCapabilityOverride,
        remove: deleteCapabilityOverride,
        onUnchanged: () =>
          toast.message(
            "That override was saved, but it didn't change the current decision. Did you mean Inherit?",
          ),
      });
      const schedChanged = await syncCapabilityPin({
        capability: SCHEDULED,
        scopeType: "source",
        scopeId: sourceId,
        current: scheduledRow,
        desired: scheduledPin,
        reason: reason.trim(),
        create: createCapabilityOverride,
        remove: deleteCapabilityOverride,
        onUnchanged: () =>
          toast.message(
            "That override was saved, but it didn't change the current decision. Did you mean Inherit?",
          ),
      });
      await queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      if (!absChanged && !schedChanged) {
        toast.message("No tripwire override changes.");
      } else {
        toast.success(`Updated tripwire overrides for ${sourceName}`);
      }
      onOpenChange(false);
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      if (error instanceof OverrideRemovedThenFailedError) {
        toast.error(OVERRIDE_REMOVED_THEN_FAILED);
      } else {
        toast.error(
          error instanceof Error ? error.message : "Failed to update tripwire",
        );
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage tripwire</DialogTitle>
          <DialogDescription>
            Source {sourceId ?? "—"}. Inherit means no override row — the more
            senior setting applies. Kill blocks manual runs too; Deny on
            scheduled only stops cron.
          </DialogDescription>
        </DialogHeader>
        {absoluteGlobalDeny ? (
          <p className="text-sm text-destructive">
            Tripwire is currently killed globally. A source-level Allow cannot
            override that.
          </p>
        ) : null}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>May tripwire touch this source at all?</Label>
            <CapabilityStateControl
              value={absolutePin}
              onChange={setAbsolutePin}
              denyLabel="Kill"
              disabled={pending}
            />
          </div>
          <div className="space-y-2">
            <Label>Should cron sweep this source?</Label>
            <CapabilityStateControl
              value={scheduledPin}
              onChange={setScheduledPin}
              denyLabel="Deny"
              disabled={pending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tripwire-reason">Reason</Label>
            <Textarea
              id="tripwire-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Required when writing an allow, deny, or kill row"
              disabled={pending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={
              pending || !sourceId || (writesNeedReason && !reason.trim())
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
